import type { CVData } from "../types";

const ACCENT = "#3B82F6";
const DARK = "#1e293b";

export function CVTemplateTech({ data }: { data: CVData }) {
  const { profile, experiences, education, skills, certificates } = data;

  return (
    <div
      className="cv-content mx-auto max-w-[820px] bg-white p-0 print:p-0"
      style={{
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        fontSize: 12,
        lineHeight: 1.6,
      }}
    >
      {/* Header with accent bar */}
      <div className="border-l-4 bg-gray-50 p-6 print:p-6" style={{ borderColor: ACCENT }}>
        <h1 className="text-2xl font-bold" style={{ color: DARK }}>
          {profile.name}
        </h1>
        <p className="mt-1 text-sm" style={{ color: ACCENT }}>
          {profile.title}
        </p>
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-600">
          {profile.email && <span>→ {profile.email}</span>}
          {profile.phone && <span>→ {profile.phone}</span>}
          {profile.address && <span>→ {profile.address}</span>}
          {profile.github_url && <span>→ github</span>}
          {profile.linkedin_url && <span>→ linkedin</span>}
        </div>
      </div>

      <div className="p-6 print:p-6">
        {profile.bio && (
          <section className="mb-5">
            <h2
              className="mb-2 text-xs font-bold uppercase tracking-wider"
              style={{ color: ACCENT }}
            >
              {"// about"}
            </h2>
            <p className="text-[13px] text-gray-700">{profile.bio}</p>
          </section>
        )}

        {experiences.length > 0 && (
          <section className="mb-5">
            <h2
              className="mb-3 text-xs font-bold uppercase tracking-wider"
              style={{ color: ACCENT }}
            >
              {"// experience"}
            </h2>
            <div className="space-y-4">
              {experiences.map((exp) => (
                <div
                  key={exp.id}
                  className="rounded-lg border p-4"
                  style={{ borderColor: "#e2e8f0" }}
                >
                  <div className="flex justify-between">
                    <span className="font-bold text-gray-900">
                      {exp.position}
                    </span>
                    <span
                      className="text-[10px] font-mono"
                      style={{ color: ACCENT }}
                    >
                      {exp.period_start} - {exp.period_end || "now"}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">{exp.company}</div>
                  <ul className="mt-2 list-disc space-y-0.5 pl-4 text-[12px] text-gray-700">
                    {exp.responsibilities.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {exp.tech_stack.map((t) => (
                      <span
                        key={t}
                        className="rounded px-2 py-0.5 text-[10px] font-mono"
                        style={{
                          background: "rgba(59, 130, 246, 0.1)",
                          color: ACCENT,
                        }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {education.length > 0 && (
          <section className="mb-5">
            <h2
              className="mb-3 text-xs font-bold uppercase tracking-wider"
              style={{ color: ACCENT }}
            >
              {"// education"}
            </h2>
            {education.map((edu) => (
              <div key={edu.id} className="mb-2 flex justify-between text-[12px]">
                <div>
                  <span className="font-semibold text-gray-900">{edu.school}</span>
                  <span className="text-gray-600"> — {edu.degree}</span>
                  {edu.grade && (
                    <span className="text-gray-500"> ({edu.grade})</span>
                  )}
                </div>
                <span className="font-mono text-[10px] text-gray-500">
                  {edu.period}
                </span>
              </div>
            ))}
          </section>
        )}

        {skills.length > 0 && (
          <section className="mb-5">
            <h2
              className="mb-3 text-xs font-bold uppercase tracking-wider"
              style={{ color: ACCENT }}
            >
              {"// skills"}
            </h2>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {skills.map((s) => (
                <div key={s.id} className="flex items-center gap-2">
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${s.level}%`,
                        background: `linear-gradient(90deg, ${ACCENT}, #06b6d4)`,
                      }}
                    />
                  </div>
                  <span className="w-24 truncate text-[11px] text-gray-700">
                    {s.name}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {certificates.length > 0 && (
          <section>
            <h2
              className="mb-3 text-xs font-bold uppercase tracking-wider"
              style={{ color: ACCENT }}
            >
              {"// certificates"}
            </h2>
            <div className="flex flex-wrap gap-2">
              {certificates.map((c) => (
                <span
                  key={c.id}
                  className="rounded border px-2 py-1 text-[11px]"
                  style={{ borderColor: "#e2e8f0", color: "#475569" }}
                >
                  {c.name}
                  {c.date && (
                    <span className="ml-1 text-gray-400">({c.date})</span>
                  )}
                </span>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
